import React, {useState, useEffect} from "react";
import {useDataWithRoute, useDebounce, useTitleFromComponent} from "../../../utils/hooks";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {VsiBubble, VsiH4, VsiLgText} from "../../../styled/App.styled";
import {Card, Col, FormGroup, Input, InputGroup, InputGroupText, Row} from "reactstrap";
import {snakeCaseToTitleCase} from "../../../utils/Helpers";
import RadarChart from "../../../components/RadarChart";
import Grid from "../../../components/Grid";
import {integerFormatter} from "../../../utils/gridFormatters";
import ColumnChart from "../../../components/ColumnChart";
import {beforeAfterOld} from "../../../utils/chartUtils";

const fieldsArr = [
    'text_size_gb',
    'db_size_gb',
    'custom_size_gb',
    'av_size_gb',
    'compress_size_gb',
    'other_size_gb'
];

const defs = [
    {
        "field": "fileType",
        "headerName": "File Type",
    },
    {
        "field": "before",
        "type": "numericColumn",
        "headerName": "Current Expenditure",
        "cellRenderer": integerFormatter,
        "aggFunc": "sum",
    },
    {
        "field": "delta",
        "type": "numericColumn",
        "headerName": "Old Files Size",
        "cellRenderer": integerFormatter,
        "aggFunc": "sum",
    },
    {
        "field": "after",
        "type": "numericColumn",
        "headerName": "After Moving",
        "filter": "agNumberColumnFilter",
        "cellRenderer": integerFormatter,
        "aggFunc": "sum",
    }
];

const toAbbrUpperTitle = field => snakeCaseToTitleCase(field.replace('_gb', '')).split(" ").map(word => word.length === 2 ? word.toUpperCase() : word).join(" ");
export default function FileSavings({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [data] = useDataWithRoute(`/api/v6/report/file_analysis_proc?client_id=${client.clientId}&period_id=${periodId}`);
    const [gridData, setGridData] = useState([]);
    const [categories, setCategories] = useState(fieldsArr.map(toAbbrUpperTitle));
    const [radarSeries, setRadarSeries] = useState([]);
    const [columnSeries, setColumnSeries] = useState([]);

    const [costPerGbBefore, setCostPerGbBefore] = useState(parseFloat(localStorage.getItem(`vsi_${client.name}_file_savings_cost_per_gb_before`) || 2.00));
    const [costPerGbAfter, setCostPerGbAfter] = useState(parseFloat(localStorage.getItem(`vsi_${client.name}_file_savings_cost_per_gb_after`) || 1.00));

    const debouncedCostPerGbBefore = useDebounce(costPerGbBefore, 250);
    const debouncedCostPerGbAfter = useDebounce(costPerGbAfter, 250);

    function setUpFileSavings(rowData) {
        const [grandTotals, oldTotals] = data?.recordsets || [[], []];

        const fullSizes = {};
        const oldSizes = {};

        const dataArr = [];

        const currSeriesData = [];
        const oldSeriesData = [];
        const afterSeriesData = [];


        for (let i = 0; i < fieldsArr.length; i++) {
            const field = fieldsArr[i];
            fullSizes[field] = grandTotals?.[0]?.[field] || 0;
            oldSizes[field] = oldTotals?.[0]?.[field] || 0;

            fullSizes['after_' + field] = fullSizes[field] - oldSizes[field];

            dataArr.push({
                'fileType': toAbbrUpperTitle(field),
                'before': fullSizes[field],
                'after': fullSizes['after_' + field],
                'delta': oldSizes[field],
                'cost_before': fullSizes[field] * costPerGbBefore,
                'cost_after': fullSizes['after_' + field] * costPerGbAfter,
                'field': field,
            });

            if (!rowData || rowData?.find?.(datum => datum.field === field)) {
                currSeriesData.push(parseFloat((fullSizes[field] * costPerGbBefore).toFixed(2)));
                oldSeriesData.push(parseFloat((oldSizes[field] * costPerGbBefore).toFixed(2)));
                afterSeriesData.push(parseFloat((fullSizes['after_' + field] * costPerGbAfter).toFixed(2)));
            }
        }

        if (rowData) {
            setCategories(fieldsArr.filter(field => {
                return rowData?.find?.(datum => datum.field === field)
            }).map(toAbbrUpperTitle))
        } else {
            setCategories(fieldsArr.map(toAbbrUpperTitle));
            setGridData(dataArr);
        }

        setRadarSeries([
            {
                name: 'Current Cost',
                data: currSeriesData,
                pointPlacement: 'on'
            },
            {
                name: 'After Moving',
                data: afterSeriesData,
                pointPlacement: 'on'
            },
            {
                name: 'Old Files Cost',
                data: oldSeriesData,
                pointPlacement: 'on'
            },
        ]);

        setColumnSeries([
            {
                name: 'Current Cost',
                data: currSeriesData,
            },
            {
                name: 'After Moving',
                data: afterSeriesData,
            },
        ]);
    }

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
        setUpFileSavings(rowData || []);
    };

    useEffect(() => {
        if (debouncedCostPerGbBefore && debouncedCostPerGbAfter) {
            setUpFileSavings();
        }
    }, [data, debouncedCostPerGbBefore, debouncedCostPerGbAfter]);

    const changeCostPerGbBefore = e => {
        const {value} = e.target;
        setCostPerGbBefore(value);
        localStorage.setItem(`vsi_${client.name}_file_savings_cost_per_gb_before`, value);
    }

    const changeCostPerGbAfter = e => {
        const {value} = e.target;
        setCostPerGbAfter(value);
        localStorage.setItem(`vsi_${client.name}_file_savings_cost_per_gb_after`, value);
    }

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>File Savings</VsiH4>
        <VsiBubble noAutobot>
            <Row>
                <Col xs={12} md={4} lg={2}>
                    <FormGroup>
                        <VsiLgText>
                            Cost per GiB Before
                        </VsiLgText>
                        <InputGroup>
                            <InputGroupText>
                                $
                            </InputGroupText>
                            <Input
                                value={costPerGbBefore}
                                onChange={changeCostPerGbBefore}
                                type="number"
                            />
                        </InputGroup>
                    </FormGroup>
                </Col>
                <Col xs={12} md={4} lg={2}>
                    <FormGroup>
                        <VsiLgText>
                            Cost per GiB After
                        </VsiLgText>
                        <InputGroup>
                            <InputGroupText>
                                $
                            </InputGroupText>
                            <Input
                                value={costPerGbAfter}
                                onChange={changeCostPerGbAfter}
                                type="number"
                            />
                        </InputGroup>
                    </FormGroup>
                </Col>
            </Row>
        </VsiBubble>
        <Row className={"mb-3"}>
            <Col lg={5} md={6} xs={12}>
                <VsiBubble autobotInfo={{ secondaryMetric: [costPerGbBefore, costPerGbAfter] }}>
                    <RadarChart series={radarSeries}
                                title={'Savings by Type'}
                                colors={beforeAfterOld}
                                valuePrefix={"$"}
                                categories={categories}/>
                </VsiBubble>
            </Col>
            <Col lg={7} md={6} xs={12}>
                <VsiBubble autobotName='VerticalBarChart' autobotInfo={{ secondaryMetric: [costPerGbBefore, costPerGbAfter] }}>
                    <ColumnChart categories={categories}
                                    title={'Savings by Type'}
                                    yAxisTitle={"Cost ($)"}
                                    colors={beforeAfterOld}
                                    valuePrefix={"$"}
                                    series={columnSeries}/>
                </VsiBubble>
            </Col>
        </Row>
        <VsiBubble>
            <Grid data={gridData} columnDefs={defs} filterChanged={filterChanged}/>
        </VsiBubble>
    </Card>
}
